import React, { useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "../styles/Geothermal.css";
import geothermalData from "./GeoThermalJson.json";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";

const spot = "/img/geothermal-spots.png";

// ✅ Utility to detect mobile view
const isMobileView = () => window.innerWidth <= 778;

// ✅ Custom marker icon with dynamic popup anchor
const createIcon = () =>
  new L.Icon({
    iconUrl: spot,
    iconSize: [35, 41],
    iconAnchor: [12, 41],
    popupAnchor: isMobileView() ? [0, 10] : [1, -34],
  });

// ✅ Helper component to pan the map
const MapFocus = ({ location }) => {
  const map = useMap();
  if (location) {
    map.flyTo(location, window.innerWidth <= 778 ? 5 : 10);
  }
  return null;
};

const GeothermalMap = () => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const markerRefs = useRef([]);
  const [searchTerm, setSearchTerm] = useState("");

  const handleLocationClick = (location, index) => {
    console.log(location, index);
    setSelectedLocation(location);
    if (markerRefs.current[index]) {
      markerRefs.current[index].openPopup();
    }
  };

  console.log(selectedLocation);

  const filteredData = geothermalData.filter(
    (spot) =>
      spot.COUNTY.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spot.REGION_LOC.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spot.SOURCE.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <LandingSection
        data={{ imageName: "geo.webp", title: "Utah Geothermal Hotspots" }}
      />

      {/* Intro section */}
      <div className="verticals_section">
        <div className="verticals-intro">
          <div className="verticals-intro-sec1">
            <h2>Utah’s Geothermal Hotspots ({filteredData.length})</h2>
          </div>
          <div className="verticals-intro-sec2">
            <p>
              Utah is home to natural hot springs and cutting-edge geothermal
              projects, making it a key region for clean, renewable energy
              development.
            </p>
            <Link to={"/about"}></Link>
          </div>
        </div>
      </div>

      <div className="geothermal-map">
        {/* Left-side list */}
        <div className="list-container">
          <h2>
            Geothermal Hotspots ({filteredData.length})<br />
            <span className="text-sm font-base">
              (*Not all hotspots are marked, only those with fully verified
              datas are displayed.)
              <div className="flex justify-end">
                <a
                  target="_blank"
                  href="https://geology.utah.gov/map-pub/maps/interactive-maps/wells-springs/"
                  rel="noreferrer"
                >
                  Hotspot Source
                </a>
              </div>
            </span>
          </h2>

          {/* Search */}
          <div className="flex justify-center items-center my-4">
            <input
              type="text"
              placeholder="Search by County, Region or Source"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input p-2 border border-gray-300 rounded-3xl w-[40rem]"
            />
          </div>

          {/* Header */}
          <div className="grid grid-cols-4 items-center gap-4 rounded-lg border-pink-700 hover:border-1 border-solid p-4 transition-all duration-200 bg-red-200/50">
            <span className="text-red-800 font-bold">Map No.</span>
            <span className="text-red-800 font-bold">County</span>
            <span className="text-red-800 font-bold">Region</span>
            <span className="text-red-800 font-bold">Source</span>
          </div>

          {/* Scrollable List */}
          <div className="list-scroll max-h-[600px] overflow-y-auto mt-2">
            <ul className="p-0 m-0 list-none">
              {filteredData.map((item, index) => (
                <li
                  key={index}
                  className="list-item"
                  onClick={() =>
                    handleLocationClick(
                      { lat: item.LAT, lng: item.LONG, details: item },
                      index
                    )
                  }
                >
                  <div className="grid grid-cols-4 items-center gap-4 text-blue-800 font-bold">
                    <span>{item.MAPNO}</span>
                    <span>{item.COUNTY}</span>
                    <span>{item.REGION_LOC || `Hotspot ${index + 1}`}</span>
                    <span>{item.SOURCE}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Map */}
        <div className="map-container">
          <MapContainer
            center={[39.32098, -111.093731]}
            zoom={7}
            minZoom={6}
            maxBounds={[
              [30, -105],
              [50, -120],
            ]}
            maxBoundsViscosity={1.0}
            className="map"
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            {selectedLocation && (
              <MapFocus
                location={[selectedLocation.lat, selectedLocation.lng]}
              />
            )}
            {filteredData
              .filter((spot) => spot.DEPTH)
              .filter((spot) => spot.FLOW)
              .map((spot, index) => (
                <Marker
                  key={index}
                  position={[spot.LAT, spot.LONG]}
                  icon={createIcon()}
                  ref={(el) => (markerRefs.current[index] = el)}
                >
                  <Popup>
                    <b>
                      <u>Geothermal Hotspot</u>
                    </b>
                    <br />
                    Region: {spot.REGION_LOC} <br />
                    County: {spot.COUNTY} <br />
                    Type: {spot.TYPE}
                    <br />
                    Map No.: {spot.MAPNO}
                    <br />
                    Depth: {spot.DEPTH}
                    <br />
                    Class: {spot.CLASS}
                    <br />
                    Reference: {spot.REFERENCE}
                    <br />
                    Source: {spot.SOURCE}
                    <br />
                    Location: {spot.LOCATION}
                  </Popup>
                </Marker>
              ))}
          </MapContainer>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default GeothermalMap;
